import { Link } from 'react-router-dom';
import { AlertTriangle, Car, Cpu, ShieldAlert, Bell, ArrowRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { ButtonLink } from '@/components/ui/Button';
import { Skeleton, SkeletonCard, SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { SeverityBadge } from '@/components/domain/SeverityBadge';
import { OnlineDot } from '@/components/domain/OnlineDot';
import { RelativeTime } from '@/components/domain/RelativeTime';
import { BaseMap, type MapMarker } from '@/components/map/BaseMap';
import { useAlerts, useCrashes, useDeviceStatus, useDevices, useSummary, useVehicles } from '@/features/shared/queries';
import { useAuth } from '@/auth/useAuth';
import { coordinates } from '@/lib/format';

function StatTile({ label, value, icon: Icon }: { label: string; value: number | undefined; icon: LucideIcon }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 text-ink-secondary">
        <Icon className="size-4" aria-hidden />
        <span className="text-[13px]">{label}</span>
      </div>
      {value === undefined ? (
        <Skeleton className="mt-2 h-7 w-12" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tracking-tight text-ink" data-numeric>
          {value}
        </p>
      )}
    </div>
  );
}

/**
 * What an owner opens the app to find out, in the order they ask it:
 * is my device reporting, where is the vehicle, did anything happen, and — the one
 * the old dashboard never answered — did the alerts actually reach anyone.
 */
export function OverviewPage() {
  const { user } = useAuth();
  const summary = useSummary();
  const vehicles = useVehicles();
  const devices = useDevices();
  const status = useDeviceStatus();
  const crashes = useCrashes(5);
  const alerts = useAlerts(50);

  const needsSetup =
    vehicles.isSuccess && devices.isSuccess && (vehicles.data.length === 0 || devices.data.length === 0);

  const positioned = (status.data ?? []).filter(
    (device): device is typeof device & { gps_lat: number; gps_lon: number } =>
      device.gps_lat !== null && device.gps_lon !== null
  );

  const markers: MapMarker[] = positioned.map((device) => ({
    id: device.id,
    position: [device.gps_lat, device.gps_lon],
    title: device.serial_number,
    subtitle: device.is_online ? 'Reporting now' : 'Last known position',
  }));

  const failedAlerts = (alerts.data ?? []).filter((alert) => alert.status === 'failed');

  return (
    <>
      <PageHeader
        title={`Welcome back, ${user?.name?.split(' ')[0] ?? 'there'}`}
        description="Crash detection status for your vehicles."
      />

      {needsSetup && (
        <Card className="mb-6 border-accent/30 bg-accent-soft">
          <CardBody className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-ink">Finish setting up</p>
              <p className="mt-0.5 text-[13px] text-ink-secondary">
                {vehicles.data?.length === 0
                  ? 'Add a vehicle and link your device so crashes can be detected.'
                  : 'Link your device so crashes can be detected.'}
              </p>
            </div>
            <ButtonLink to="/setup" variant="primary" size="sm" className="gap-1.5">
              Continue setup
              <ArrowRight className="size-4" aria-hidden />
            </ButtonLink>
          </CardBody>
        </Card>
      )}

      {failedAlerts.length > 0 && (
        <Card className="mb-6 border-bad/30 bg-bad-soft">
          <CardBody>
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-bad" aria-hidden />
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">
                  {failedAlerts.length} alert{failedAlerts.length === 1 ? '' : 's'} could not be
                  delivered
                </p>
                <p className="mt-0.5 truncate text-[13px] text-ink-secondary">
                  {failedAlerts[0]?.last_error ?? 'The delivery provider rejected the message.'}
                </p>
                <Link
                  to="/alerts"
                  className="mt-2 inline-block text-[13px] font-medium text-accent hover:text-accent-hover"
                >
                  Review alert delivery
                </Link>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Vehicles" value={summary.data?.total_vehicles} icon={Car} />
        <StatTile label="Devices" value={summary.data?.total_devices} icon={Cpu} />
        <StatTile label="Crash events" value={summary.data?.total_crashes} icon={ShieldAlert} />
        <StatTile label="Alerts in flight" value={summary.data?.active_alerts} icon={Bell} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Where your vehicles are"
            description="Last reported position. Updated every 30 seconds."
          />
          {status.isPending ? (
            <div className="p-4">
              <Skeleton className="h-80 w-full" />
            </div>
          ) : status.isError ? (
            <ErrorState error={status.error} onRetry={status.refetch} />
          ) : markers.length === 0 ? (
            <EmptyState
              icon={Car}
              title="No position reported yet"
              description="Your device sends its location once it powers on and gets a GPS fix."
            />
          ) : (
            <div className="p-4">
              <BaseMap markers={markers} />
            </div>
          )}
        </Card>

        <Card>
          <CardHeader title="Your devices" />
          {status.isPending ? (
            <SkeletonRows rows={2} />
          ) : (status.data ?? []).length === 0 ? (
            <EmptyState
              icon={Cpu}
              title="No devices linked"
              description="Link the device that came with your AutoAware kit."
              action={
                <ButtonLink to="/setup" variant="primary" size="sm">
                  Link a device
                </ButtonLink>
              }
            />
          ) : (
            <ul className="divide-y divide-line">
              {(status.data ?? []).map((device) => (
                <li key={device.id}>
                  <Link
                    to={`/devices/${device.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-surface-sunken"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-medium text-ink">
                        {device.serial_number}
                      </p>
                      <p className="mt-0.5 text-[12px] text-ink-muted">
                        {coordinates(device.gps_lat, device.gps_lon)}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <OnlineDot online={device.is_online} />
                      <p className="mt-0.5 text-[12px] text-ink-muted">
                        <RelativeTime value={device.last_seen} />
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader
          title="Recent crash events"
          action={
            <Link to="/crashes" className="text-[13px] font-medium text-accent hover:text-accent-hover">
              View all
            </Link>
          }
        />
        {crashes.isPending ? (
          <SkeletonRows rows={3} />
        ) : crashes.isError ? (
          <ErrorState error={crashes.error} onRetry={crashes.refetch} />
        ) : (crashes.data ?? []).length === 0 ? (
          <EmptyState
            icon={ShieldAlert}
            title="No crashes detected"
            description="This is the state you want. Detected impacts will appear here with the alerts they triggered."
          />
        ) : (
          <ul className="divide-y divide-line">
            {(crashes.data ?? []).map((crash) => (
              <li key={crash.id}>
                <Link
                  to={`/crashes/${crash.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-surface-sunken"
                >
                  <div className="flex items-center gap-3">
                    <SeverityBadge severity={crash.severity} />
                    <span className="text-[13px] text-ink">
                      {coordinates(crash.gps_lat, crash.gps_lon)}
                    </span>
                  </div>
                  <RelativeTime value={crash.timestamp} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {summary.isError && (
        <div className="mt-4">
          <ErrorState error={summary.error} onRetry={summary.refetch} title="Summary unavailable" />
        </div>
      )}
      {vehicles.isPending && <SkeletonCard />}
    </>
  );
}
